import { IFormValues } from '@interfaces/buildPackage';
import { getFlightFees } from '@v2/views/checkout/containers/CheckoutAsideCards/components/util';
import { getEffectivePassengerCount } from '@v2/views/checkout/components/CheckoutForm/utils';
import type { SelectedTicketDataType } from '@v2/views/checkout/Checkout';
import type { PreBookingPayload } from '@v2/views/flights/components/FlightsContent/FlightDrawer/FlightFares';

export type PackageHeaderSummaryInput = {
  travelers: number;
  ticketTotal: number;
  hotelTotal: number;
  flightTotal: number;
};

export type SearchHeaderSummaryInput = {
  includeTickets: boolean;
  includeHotel: boolean;
  includeFlights: boolean;
  selectedTicketPrice: number;
  minTicketPrice: number;
  roomPrices: number[];
  hasRoomSelections: boolean;
  minHotelPrice: number;
  flightPackagePrice: number | null;
  packageData: IFormValues | null;
  flightData: {
    passengers?: PreBookingPayload['passengers'];
  } | null;
  ticketData?: { quantity?: number } | null;
};

export function buildSearchHeaderSummary({
  includeTickets,
  includeHotel,
  includeFlights,
  selectedTicketPrice,
  minTicketPrice,
  roomPrices,
  hasRoomSelections,
  minHotelPrice,
  flightPackagePrice,
  packageData,
  flightData,
  ticketData,
}: SearchHeaderSummaryInput) {
  const ticketTotal = includeTickets
    ? selectedTicketPrice || minTicketPrice || 0
    : 0;
  const hotelTotal = includeHotel
    ? hasRoomSelections
      ? roomPrices.reduce((sum, price) => sum + Number(price || 0), 0)
      : minHotelPrice || 0
    : 0;
  const flightTotal =
    includeFlights && flightPackagePrice
      ? flightPackagePrice + getFlightFees(flightPackagePrice)
      : 0;

  return getPackageHeaderTotals({
    travelers: getEffectivePassengerCount(
      {
        tickets: 1,
        hotelStartDate: '',
        hotelEndDate: '',
        roomConfigurations: [],
        ...packageData,
        selected: {
          tickets: includeTickets,
          hotel: includeHotel,
          flights: includeFlights,
        },
      },
      flightData as PreBookingPayload | null,
      ticketData as SelectedTicketDataType
    ),
    ticketTotal,
    hotelTotal,
    flightTotal,
  });
}

export function getPackageHeaderTotals({
  travelers,
  ticketTotal,
  hotelTotal,
  flightTotal,
}: PackageHeaderSummaryInput) {
  const total = ticketTotal + hotelTotal + flightTotal;
  const safeTravelers = travelers > 0 ? travelers : 1;

  return {
    total,
    perPerson: total / safeTravelers,
    travelers: safeTravelers,
  };
}
